/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Divider from '@mui/material/Divider';
import { alpha, useTheme } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import { bgGradient } from '../../theme/css';
import Logo from '../components/Logo';
import { useDispatch, useSelector } from 'react-redux';
import { auth, grantToken, login } from '@ink-ai/portal/reducers/auth';
import { RootState, getStore } from '@ink-ai/portal/reducers';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const receiveCode = (event: MessageEvent) => {
  if (event.origin !== window.location.origin) {
    return;
  }
  const data = event.data;
  if (data?.source !== 'ink-ai-oidc') {
    return;
  }
  const store = getStore();
  console.log('Received secret key:', data.code);
  store.dispatch(
    grantToken({
      grant_type: 'authorization_code',
      code: data.code,
    }),
  );
};

window.addEventListener('message', receiveCode, false);

export const Login = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch<any>();
  const authState = useSelector((state: RootState) => state.auth);
  const appState = useSelector((state: RootState) => state.app);
  const location = useLocation();
  const navigate = useNavigate();

  const onLoginClick = () => {
    dispatch(auth.actions.changeLoading(true));
    const oidcPage = `${appState.appParam.authorization_endpoint}?scope=openid profile email&response_type=code&client_id=${appState.appParam.cognito_client_id}&redirect_uri=${appState.addInHost}/oidc.html`;
    console.log('Open cognito page: ', oidcPage);
    const loginPopup = window.open(oidcPage, 'Login', 'width=600,height=400');
    const checkPopupClosed = setInterval(() => {
      if (loginPopup.closed) {
        clearInterval(checkPopupClosed);
        if (!getStore().getState().auth.grantTokenLoading) {
          dispatch(auth.actions.changeLoading(false));
        }
      }
    }, 1000);
  };

  useEffect(() => {
    if (!authState.idToken) {
      return;
    }
    if (
      authState.expiresAt &&
      authState.expiresAt * 1000 <= Date.now() &&
      authState.refreshToken
    ) {
      console.log('access token expired, refreshing token');
      dispatch(
        grantToken({
          grant_type: 'refresh_token',
          refresh_token: authState.refreshToken,
        }),
      );
      return;
    } else {
      dispatch(login());
    }
  }, [authState.idToken]);

  useEffect(() => {
    if (authState.loggedIn) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [authState.loggedIn]);

  return (
    <Box
      sx={{
        ...bgGradient({
          color: alpha(theme.palette.background.default, 0.9),
          imgUrl: '/images/overlay_4.jpg',
        }),
        height: '100vh',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/images/overlay_4.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.1,
          zIndex: 0,
        }}
      />

      <Logo
        sx={{
          position: 'fixed',
          top: { xs: 16, md: 24 },
          left: { xs: 16, md: 24 },
          zIndex: 1,
        }}
      />

      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: 1, position: 'relative', zIndex: 1 }}
      >
        <Box sx={{ width: '100%', maxWidth: 420, textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}
          >
            <LockIcon sx={{ color: 'primary.main' }} />
          </Box>
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}
          >
            {t('solutionName')}
          </Typography>
        </Box>

        <Card
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 420,
            backdropFilter: 'blur(8px)',
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
            boxShadow: (theme) =>
              `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
          }}
        >
          <Typography variant="h4" align="center" sx={{ mb: 1 }}>
            {t('common.welcome')}
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            {t('common.signInToContinue')}
          </Typography>

          <Box sx={{ position: 'relative', my: 3 }}>
            <Divider>
              <Typography
                variant="caption"
                sx={{ px: 2, textTransform: 'uppercase' }}
              >
                {t('common.secureLogin')}
              </Typography>
            </Divider>
          </Box>

          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            onClick={onLoginClick}
            loading={authState.loading}
          >
            {authState.loading ? t('common.authenticating') : t('common.login')}
          </LoadingButton>
        </Card>
      </Stack>
    </Box>
  );
};
